'use server'

import { apiRequest, ApiResponse } from '../request'

const BASE = 'financial/cash-register'

export type CashRegisterDirection = 'IN' | 'OUT'
export type CashRegisterEntrySource = 'AUTO' | 'MANUAL'
export type CashRegisterScope = 'company' | 'organization'

/**
 * Uma linha do caixa. A automática soma as cobranças pagas (ou estornadas) da
 * unidade no dia e na forma de pagamento; a manual é um lançamento avulso.
 */
export interface CashRegisterEntry {
    id: number
    companyId: number
    companyName: string | null
    cashDate: string
    paymentMethod: string
    direction: CashRegisterDirection
    source: CashRegisterEntrySource
    /** Centavos, sempre positivo — o sinal vem de `direction`. */
    amount: number
    paymentCount: number
    description: string | null
    /** Vendedor. Na saída manual, quem fez a retirada; no estorno, quem vendeu. */
    seller: { id: number; name: string } | null
    createdBy: { id: number; name: string } | null
    createdAt: string
    updatedAt: string
}

export interface CashRegisterMethodTotal {
    paymentMethod: string
    in: number
    out: number
    balance: number
}

export interface CashRegisterDay {
    date: string
    totals: { in: number; out: number; balance: number }
    methods: CashRegisterMethodTotal[]
    entries: CashRegisterEntry[]
}

export interface CashRegisterEntryBilling {
    id: number
    description: string | null
    amount: number
    paidAmount: number | null
    paidAt: string | null
    refundedAmount: number | null
    status: string
    client: { id: number; name: string } | null
}

export interface CashRegisterEntryInput {
    cashDate: string
    paymentMethod: string
    direction: CashRegisterDirection
    amount: number
    sellerId?: number | null
    description?: string
}

export async function getCashRegisterDay(params: {
    date?: string
    paymentMethod?: string
    sellerId?: number | null
    scope?: CashRegisterScope
}): Promise<ApiResponse<CashRegisterDay>> {
    return apiRequest<CashRegisterDay>(BASE, {
        params: {
            ...(params.date ? { date: params.date } : {}),
            ...(params.paymentMethod
                ? { paymentMethod: params.paymentMethod }
                : {}),
            ...(params.sellerId ? { sellerId: params.sellerId } : {}),
            ...(params.scope === 'organization'
                ? { scope: 'organization' }
                : {}),
        },
    })
}

/** O dia de caixa aberto agora na unidade ativa (horário de fechamento aplicado). */
export async function getCashRegisterCurrentDate(): Promise<
    ApiResponse<{ date: string }>
> {
    return apiRequest<{ date: string }>(`${BASE}/current-date`, {})
}

export async function getCashRegisterEntryBillings(
    id: number
): Promise<ApiResponse<CashRegisterEntryBilling[]>> {
    return apiRequest<CashRegisterEntryBilling[]>(`${BASE}/${id}/billings`, {})
}

export async function createCashRegisterEntry(
    data: CashRegisterEntryInput
): Promise<ApiResponse<CashRegisterEntry>> {
    return apiRequest<CashRegisterEntry>(BASE, { data }, 'post')
}

export async function updateCashRegisterEntry(
    id: number,
    data: Partial<CashRegisterEntryInput>
): Promise<ApiResponse<CashRegisterEntry>> {
    return apiRequest<CashRegisterEntry>(`${BASE}/${id}`, { data }, 'patch')
}

export async function deleteCashRegisterEntry(
    id: number
): Promise<ApiResponse<{ id: number }>> {
    return apiRequest<{ id: number }>(`${BASE}/${id}`, {}, 'delete')
}
